
import Vue from 'vue';
import OfferingCard from '@/components/OfferingCard.vue';
import WhiteChip from '@/components/base/WhiteChip.vue';
import { KitToolLabel as NodeDefLabel } from '@run-diffusion/shared';
import { RunnitNodeDef, RunnitNodeField, RunnitNodeFieldGroup } from '@run-diffusion/shared';
import { KitsMixin } from '@/mixins/KitsMixin';
import { v4 as uuidv4 } from 'uuid';
import OfferingCardTokenChip, { createTokenCostLabel } from '@/components/OfferingCardTokenChip.vue';

export default Vue.extend({
	name: 'OfferingCardWithLabels',
	mixins: [
		KitsMixin,
	],
	components: {
		OfferingCard,
		WhiteChip,
		OfferingCardTokenChip,
	},
	props: {
		// OfferingCard props
		horizontal: { type: Boolean, default: false },
		small: { type: Boolean, default: false },
		selected: { type: Boolean, default: false },
		title: { type: String, default: '' },
		imageUrl: { type: String, default: '' },
		avatar: { type: [Object, String], default: null },
		description: { type: String, default: '' },
		isFeatured: { type: Boolean, default: false },
		newUntil: { type: [Object, Date], default: null },

		// Props for generating labels
		runnitNodeDef: {
			type: Object as () => RunnitNodeDef,
			required: true,
		},
		existingLabels: {
			type: Array as () => NodeDefLabel[],
			default: () => []
		},
		imageHeightWidth: { type: Number, default: 74 },
	},
	computed: {
		offeringCardProps () {
			// Pass through all OfferingCard props except runnitNodeDef, existingLabels, and newUntil
			const { runnitNodeDef, existingLabels, newUntil, ...props } = this.$props;
			return props;
		},
		labels (): NodeDefLabel[] {
			return this.generateLabelsForNodeDef(this.runnitNodeDef, this.existingLabels);
		},
		showNewTag (): boolean {
			if (!this.newUntil) return false;

			let newUntilDate: Date;
			if (this.newUntil instanceof Date) {
				newUntilDate = this.newUntil;
			} else if (this.newUntil.toMillis && typeof this.newUntil.toMillis === 'function') {
				// Handle Firebase Timestamp objects (same way as publishedAt)
				newUntilDate = new Date(this.newUntil.toMillis());
			} else if (this.newUntil.toDate && typeof this.newUntil.toDate === 'function') {
				// Handle Firebase Timestamp objects (alternative method)
				newUntilDate = this.newUntil.toDate();
			} else {
				return false;
			}

			return new Date() < newUntilDate;
		},
	},
	methods: {
		generateLabelsForNodeDef (runnitNodeDef: RunnitNodeDef, existingLabels?: NodeDefLabel[]): NodeDefLabel[] {
			// Start with existing hardcoded labels or get them from kit models
			let labels: NodeDefLabel[] = [];

			if (existingLabels && existingLabels.length > 0) {
				// Use provided labels
				labels = [...existingLabels];
			} else {
				// Fall back to the Kit Tool Labels curated for this tool, which
				// now come from the runnitKits binding rather than a hardcoded
				// scan of the kit arrays.
				const curatedLabels = this.getKitToolLabels(runnitNodeDef.id);
				labels = curatedLabels ? [...curatedLabels] : [];
			}

			// Remove any existing token, timing, and visual prompt labels since we'll regenerate them from database
			labels = labels.filter(label =>
				label.icon !== 'custom-tokens-icon' &&
				label.icon !== 'mdi-clock-outline' &&
				label.icon !== 'mdi-draw'
			);

			// Add timing label from database (if not already present)
			const hasTimingLabel = labels.some(label => label.icon === 'mdi-clock-outline');
			const averageGenInSeconds = runnitNodeDef.averageGenInSeconds;
			if (!hasTimingLabel && averageGenInSeconds && averageGenInSeconds > 0) {
				const timeInSeconds = averageGenInSeconds;
				let timeLabel: string;

				if (timeInSeconds >= 60) {
					const minutes = Math.ceil((timeInSeconds / 60) * 100) / 100;
					timeLabel = `${parseFloat(minutes.toFixed(2))}m`;
				} else {
					const roundedSeconds = Math.ceil(timeInSeconds * 100) / 100;
					timeLabel = `${parseFloat(roundedSeconds.toFixed(2))}s`;
				}

				labels.push({
					id: uuidv4(),
					icon: 'mdi-clock-outline',
					label: timeLabel,
					value: timeInSeconds,
					type: 'DEFAULT',
				});
			}

			// Add cost label from database if costPerResult is available
			const tokenCostLabel = createTokenCostLabel(runnitNodeDef.costPerResult);
			if (tokenCostLabel) {
				labels.push(tokenCostLabel);
			}

			// Add visual prompt label if any field has visual prompt enabled
			const hasVisualPromptLabel = labels.some(label => label.icon === 'mdi-draw');
			if (!hasVisualPromptLabel && this.hasVisualPromptField(runnitNodeDef.fields)) {
				labels.push({
					id: uuidv4(),
					icon: 'mdi-draw',
					label: 'Annotate',
					type: 'DEFAULT',
					tooltip: 'This tool supports visual prompt annotations. After selecting an image, click the draw icon to add annotations with drawings and text. These annotations guide the image generation process.',
				});
			}

			// Sort labels by icon for consistent ordering
			return this.sortLabelsByIcon(labels);
		},


		sortLabelsByIcon (labels: NodeDefLabel[]): NodeDefLabel[] {
			// Define the desired order of icons
			const iconOrder = [
				'mdi-clock-outline',      // Timing first
				'custom-tokens-icon',     // Cost second
				'mdi-draw',               // Visual prompt third
				'mdi-weight-lifter',      // Models fourth
				'mdi-aspect-ratio',       // Special features fifth
				'mdi-fire',              // Popular sixth
			];

			return labels.sort((a, b) => {
				const aIndex = iconOrder.indexOf(a.icon);
				const bIndex = iconOrder.indexOf(b.icon);

				// If both icons are in the order array, sort by their position
				if (aIndex !== -1 && bIndex !== -1) {
					return aIndex - bIndex;
				}

				// If only one is in the order array, it comes first
				if (aIndex !== -1) return -1;
				if (bIndex !== -1) return 1;

				// If neither is in the order array, maintain original order
				return 0;
			});
		},

		getLabelClass (type?: string, icon?: string): string {
			if (type === 'HIGHLIGHT') {
				return 'offering-label--highlight';
			}
			if (type === 'POPULAR') {
				return 'offering-label--popular';
			}
			if (icon === 'mdi-draw') {
				return 'offering-label--visual-prompt';
			}
			return 'offering-label--default';
		},
		getLabelIconColor (type?: string, icon?: string): string {
			if (type === 'HIGHLIGHT') {
				return 'var(--primary-blue-opacity-60)';
			}
			if (type === 'POPULAR') {
				return 'var(--primary-purple-2)';
			}
			if (icon === 'mdi-draw') {
				return 'var(--primary-blue-2)';
			}
			return 'var(--grey-500)';
		},

		hasVisualPromptField (fields?: (RunnitNodeFieldGroup | RunnitNodeField)[]): boolean {
			if (!fields || !fields.length) return false;

			for (const field of fields) {
				const f = field as RunnitNodeField;
				const g = field as RunnitNodeFieldGroup;

				// Check if it's a field (not a group)
				if ((f as any)?.__rfield) {
					// Check if it's an IMG type field with visual prompt enabled
					if (f.type === 'IMG' && (f.display as any)?.hasVisualPrompt) {
						return true;
					}
				}
				if (f.type === 'IMGS' && (f.display as any)?.hasVisualPrompt) {
					return true;
				}

				// Check if it's a group and recursively check its fields
				if ((g as any)?.__rgroup && (g as any)?.fields) {
					if (this.hasVisualPromptField((g as any).fields)) {
						return true;
					}
				}
			}

			return false;
		},
	},
});
